import type { CalendarDate } from "@internationalized/date";
import { Option, pipe } from "effect";
import {
  isoWeekday,
  parseCalendarDate,
  toAbsoluteIso,
  toTimeSlot,
} from "#/modules/les.planning/domain/calendar";
import type { LesplanningLes } from "#/modules/les.planning/domain/les";
import type { components } from "#/schema";

/** A les in the school rooster: one or more klassen in a lesuur on a weekdag, and who gives it. */
export type Roosterregel = components["schemas"]["RoosterregelModel"];
/** A docent of the school, who can give a les of the rooster. */
export type RoosterDocent = components["schemas"]["RoosterDocentModel"];

export interface RoosterregelInput {
  klasIds: string[];
  weekdag: number;
  lesuurId: string;
  geldigVanaf: string;
  geldigTot: string | null;
  /** Who gives it. Null on a new les is the docent who adds it; on a change, whoever gave it. */
  schooldocentId: string | null;
}

/** An occurrence of a roosteruur on a concrete day that no les has filled yet. */
export interface RoosterSlot {
  readonly key: string;
  readonly regel: Roosterregel;
  readonly day: CalendarDate;
  readonly startAt: string;
  readonly endAt: string;
  readonly klassen: { id: string; name: string }[];
}

/** Whether the docent gives lessen of the rooster: then a les starts from its lesuur. */
export const hasOwnRooster = (regels: readonly Roosterregel[]): boolean =>
  regels.some((regel) => regel.isCurrentUser);

export const toRegelInput = (regel: Roosterregel): RoosterregelInput => ({
  klasIds: regel.klassen.map((klas) => klas.id),
  weekdag: regel.weekdag,
  lesuurId: regel.lesuurId,
  geldigVanaf: regel.geldigVanaf,
  geldigTot: regel.geldigTot ?? null,
  schooldocentId: regel.schooldocentId,
});

/** Whose lessen and roosteruren Mijn lesdag and Mijn lesweek show. */
export interface ShownDocenten {
  /** The colleagues whose lessen show next to the docent's own. */
  readonly colleagueIds: ReadonlySet<string>;
  /** Someone who is no docent of the school, such as an admin, has no own lessen: they see all. */
  readonly everyone: boolean;
}

export const getShownDocenten = (
  docenten: readonly RoosterDocent[],
  colleagueIds: readonly string[],
): ShownDocenten => ({
  colleagueIds: new Set(colleagueIds),
  everyone: docenten.length > 0 && !docenten.some((docent) => docent.isCurrentUser),
});

export const isShownDocent = (
  shown: ShownDocenten,
  docent: Pick<RoosterDocent, "schooldocentId" | "isCurrentUser">,
): boolean =>
  shown.everyone || docent.isCurrentUser || shown.colleagueIds.has(docent.schooldocentId);

/**
 * The rooster as the docent just changed it, before the server has answered: regels changed or
 * taken away (null) by id, and the ones being made.
 */
export const applyRegelEdits = (
  regels: readonly Roosterregel[],
  edits: Readonly<Record<string, Partial<Roosterregel> | null>>,
  created: readonly Roosterregel[],
): Roosterregel[] => [
  ...regels.flatMap((regel) => {
    const edit = edits[regel.id];
    if (edit === null) return [];
    return [edit ? { ...regel, ...edit } : regel];
  }),
  ...created,
];

export const regelAppliesOn = (regel: Roosterregel, day: CalendarDate): boolean => {
  if (regel.weekdag !== isoWeekday(day)) return false;
  const startsInTime = pipe(
    parseCalendarDate(regel.geldigVanaf),
    Option.exists((from) => day.compare(from) >= 0),
  );
  const hasEnded = pipe(
    parseCalendarDate(regel.geldigTot),
    Option.exists((until) => day.compare(until) > 0),
  );
  return startsInTime && !hasEnded && !regel.uitzonderingen.includes(day.toString());
};

/** A les fills a roosteruur when it is for one of its klassen, that day, in its lesuur or time. */
const fills = (les: LesplanningLes, regel: Roosterregel, day: CalendarDate, timeZone?: string) => {
  if (!les.klassen.some((klas) => regel.klassen.some((other) => other.id === klas.id)))
    return false;
  const taken = toTimeSlot(les.startAt, les.duurMinuten, timeZone);
  if (taken.day.compare(day) !== 0) return false;
  if (les.lesuurId != null) return les.lesuurId === regel.lesuurId;
  return (
    taken.startMinutes < regel.startMinuten + regel.duurMinuten &&
    taken.endMinutes > regel.startMinuten
  );
};

/**
 * Roosteruren on these days that still wait for a les. With `now`, those that have ended are left
 * out; without it they stay, so a lesuur that has passed can still be started.
 */
export const getOpenSlots = (
  regels: readonly Roosterregel[],
  days: readonly CalendarDate[],
  lessen: readonly LesplanningLes[],
  timeZone?: string,
  now?: Date,
): RoosterSlot[] =>
  days.flatMap((day) =>
    regels.flatMap((regel): RoosterSlot[] => {
      if (!regelAppliesOn(regel, day)) return [];
      const startAt = toAbsoluteIso(day, regel.startMinuten, timeZone);
      const endAt = toAbsoluteIso(day, regel.startMinuten + regel.duurMinuten, timeZone);
      const ended = now !== undefined && new Date(endAt).getTime() < now.getTime();
      if (ended || lessen.some((les) => fills(les, regel, day, timeZone))) return [];
      return [
        {
          key: `${regel.id}:${day.toString()}`,
          regel,
          day,
          startAt,
          endAt,
          klassen: regel.klassen,
        },
      ];
    }),
  );

/** How often a week every klas has gym by the school rooster. */
export const getGymPerKlas = (
  regels: readonly Roosterregel[],
): { id: string; name: string; aantal: number }[] => {
  const perKlas = new Map<string, { id: string; name: string; aantal: number }>();
  for (const regel of regels) {
    for (const klas of regel.klassen) {
      const current = perKlas.get(klas.id);
      perKlas.set(klas.id, { ...klas, aantal: (current?.aantal ?? 0) + 1 });
    }
  }
  return [...perKlas.values()].sort((a, b) =>
    a.name.localeCompare(b.name, "nl", { numeric: true }),
  );
};

/** The first name, or the whole name of a docent without one. */
export const getFirstName = (docent: Pick<RoosterDocent, "firstName" | "name">) =>
  docent.firstName.trim() || docent.name;

/** The first name, with the last name's initial when another docent of the school shares it. */
export const getShortName = (docent: RoosterDocent, docenten: readonly RoosterDocent[]) => {
  const firstName = getFirstName(docent);
  const initial = docent.lastName.trim().charAt(0).toUpperCase();
  const shared = docenten.some(
    (other) =>
      other.schooldocentId !== docent.schooldocentId &&
      getFirstName(other).localeCompare(firstName, "nl", { sensitivity: "base" }) === 0,
  );
  return shared && initial ? `${firstName} ${initial}.` : firstName;
};
